import React from 'react';
import { Typography, Grid, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';
import { formatThaiCurrency } from '../utils';
import {
  FilterAccordion,
  FilterAccordionSummary,
  FilterAccordionDetails,
  PriceInput,
  PriceInputContainer,
  PriceButtonScrollContainer,
  PriceButton,
  Divider
} from '../styledComponents';
import { ExpandMoreIcon } from './ExpandMoreIcon';

interface PriceSectionProps {
  isExpanded: boolean;
  isVisible: boolean;
  onToggle: (event: React.MouseEvent) => void;
  priceMin: number;
  priceMax: number;
  minInputValue: string;
  maxInputValue: string;
  priceErrorMessage?: string;
  priceRangesMin: number[];
  priceRangesMax: number[];
  onMinInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMinPriceSelect: (price: number) => void;
  onMaxPriceSelect: (price: number) => void;
  onMinInputClear?: () => void; // Add clear handler for min input
  onMaxInputClear?: () => void; // Add clear handler for max input
  singleColumn?: boolean; // เพิ่ม prop สำหรับแสดงเป็น single column
  onCollapseAfterSelect?: () => void; // เพิ่ม callback สำหรับปิด accordion หลังเลือก preset price
}

export const PriceSection: React.FC<PriceSectionProps> = ({
  isExpanded,
  isVisible,
  onToggle,
  priceMin,
  priceMax,
  minInputValue,
  maxInputValue,
  priceErrorMessage,
  priceRangesMin,
  priceRangesMax,
  onMinInputChange,
  onMaxInputChange,
  onMinPriceSelect,
  onMaxPriceSelect,
  onMinInputClear,
  onMaxInputClear,
  singleColumn = false,
  onCollapseAfterSelect,
}) => {
  const { t } = useTranslation();

  // Handle min price select (no auto-close for price filter)
  const handleMinPriceSelect = (price: number) => {
    onMinPriceSelect(price);
    
    // Price filter stays open for multiple selections
  };

  // Handle max price select (no auto-close for price filter)
  const handleMaxPriceSelect = (price: number) => {
    onMaxPriceSelect(price);
    
    // Price filter stays open for multiple selections
  };

  if (!isVisible) return null;

  return (
    <>
      <FilterAccordion className={isExpanded ? 'expanded' : ''}>
        <FilterAccordionSummary 
          className={isExpanded ? 'expanded' : ''}
          onClick={onToggle}
        >
          <Typography className="section-title" variant="subtitle1" fontWeight="medium">
            {t('price')}
          </Typography>
          <ExpandMoreIcon className="expand-icon" />
        </FilterAccordionSummary>
        {isExpanded && (
          <FilterAccordionDetails className="price-section">
            <Grid container spacing={2}>
              <Grid item xs={singleColumn ? 12 : 6}>
                <PriceInputContainer>
                  <PriceInput
                    placeholder={t('min_price')}
                    value={minInputValue}
                    onChange={onMinInputChange}
                    endAdornment={minInputValue && onMinInputClear && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onMinInputClear}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  />
                </PriceInputContainer>
                <PriceButtonScrollContainer>
                  {priceRangesMin.map((price) => (
                    <PriceButton 
                      key={`min-${price}`}
                      onClick={() => handleMinPriceSelect(price)}
                    >
                      {price === 0 ? t('select') : formatThaiCurrency(price) + ' ' + t('THB')}
                    </PriceButton>
                  ))}
                </PriceButtonScrollContainer>
              </Grid>
              <Grid item xs={singleColumn ? 12 : 6}>
                <PriceInputContainer>
                  <PriceInput
                    placeholder={t('max_price')}
                    value={maxInputValue}
                    onChange={onMaxInputChange}
                    endAdornment={maxInputValue && onMaxInputClear && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onMaxInputClear}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  />
                </PriceInputContainer>
                {priceErrorMessage && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#FF0000', 
                      fontSize: '12px',
                      marginTop: '4px',
                      display: 'block'
                    }}
                  >
                    {priceErrorMessage}
                  </Typography>
                )}
                <PriceButtonScrollContainer>
                  {priceRangesMax.map((price) => (
                    <PriceButton 
                      key={`max-${price}`}
                      onClick={() => handleMaxPriceSelect(price)}
                    >
                      {formatThaiCurrency(price) + ' ' + t('THB')}
                    </PriceButton>
                  ))}
                </PriceButtonScrollContainer>
              </Grid>
            </Grid>
          </FilterAccordionDetails>
        )}
      </FilterAccordion>
      <Divider />
    </>
  );
}; 